import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "assessment-ui-scale";
const SCALE_STEP = 0.125;
const MIN_SCALE = 0.875;
const MAX_SCALE = 1.25;

const clampScale = (value: number) =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

interface ZoomControlsProps {
  compact?: boolean; // If true, shows minimal UI (no percentage display)
  showLabel?: boolean; // If true, shows "Text Size" label
}

const ZoomControls = ({
  compact = false,
  showLabel = false
}: ZoomControlsProps) => {
  const { t } = useTranslation();
  const baseFontSizeRef = useRef<number | null>(null);
  const previousInlineFontSizeRef = useRef<string>("");
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const root = document.documentElement;
    previousInlineFontSizeRef.current = root.style.fontSize;

    const computedFontSize = Number.parseFloat(
      window.getComputedStyle(root).fontSize
    );
    baseFontSizeRef.current = Number.isFinite(computedFontSize)
      ? computedFontSize
      : 16;

    const savedScale = Number.parseFloat(
      window.localStorage.getItem(STORAGE_KEY) || "1"
    );

    if (Number.isFinite(savedScale)) {
      setScale(clampScale(savedScale));
    }

    return () => {
      root.style.fontSize = previousInlineFontSizeRef.current;
    };
  }, []);

  useEffect(() => {
    if (!baseFontSizeRef.current) return;

    const root = document.documentElement;
    root.style.fontSize = `${baseFontSizeRef.current * scale}px`;
    window.localStorage.setItem(STORAGE_KEY, String(scale));
  }, [scale]);

  const increaseScale = () =>
    setScale((current) => clampScale(current + SCALE_STEP));
  const decreaseScale = () =>
    setScale((current) => clampScale(current - SCALE_STEP));
  const resetScale = () => setScale(1);

  if (compact) {
    return (
      <div className="flex h-10 items-center gap-1.5 rounded-md border border-secondary/20 bg-bkg px-2 shadow-sm">
        <button
          type="button"
          onClick={decreaseScale}
          disabled={scale <= MIN_SCALE}
          aria-label={t("assessment.zoom.decreaseTextSize")}
          title={t("assessment.zoom.decreaseTextSize")}
          className="flex h-[30px] min-w-[30px] items-center justify-center rounded-md border border-secondary/20 px-2 text-xs font-semibold text-secondary transition-colors hover:bg-secondary/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          A−
        </button>
        <button
          type="button"
          onClick={resetScale}
          aria-label={t("assessment.zoom.resetTextSize")}
          title={t("assessment.zoom.resetTextSize")}
          className="flex h-[30px] min-w-[30px] items-center justify-center rounded-md border border-secondary/20 px-2 text-xs font-semibold text-secondary transition-colors hover:bg-secondary/10"
        >
          A
        </button>
        <button
          type="button"
          onClick={increaseScale}
          disabled={scale >= MAX_SCALE}
          aria-label={t("assessment.zoom.increaseTextSize")}
          title={t("assessment.zoom.increaseTextSize")}
          className="flex h-[30px] min-w-[30px] items-center justify-center rounded-md border border-secondary/20 px-2 text-xs font-semibold text-secondary transition-colors hover:bg-secondary/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          A+
        </button>
        {!compact && (
          <div className="min-w-10 text-center text-xs font-medium text-secondary/70 ml-1">
            {Math.round(scale * 100)}%
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-10 items-center gap-1 sm:gap-1.5 rounded-md border border-secondary/20 bg-bkg px-2 sm:px-2.5 shadow-sm dark:border-secondary/30">
      {showLabel && (
        <span className="hidden md:inline text-sm font-medium text-secondary/70 mr-0.5">
          {t("assessment.zoom.textSize")}
        </span>
      )}
      <button
        type="button"
        onClick={decreaseScale}
        disabled={scale <= MIN_SCALE}
        aria-label={t("assessment.zoom.decreaseInterfaceSize")}
        className="flex h-[24px] min-w-[24px] sm:min-w-[24px] items-center justify-center rounded-md border border-secondary/20 px-2 sm:px-2.5 text-xs sm:text-sm font-semibold text-secondary transition-colors hover:bg-secondary/10 disabled:cursor-not-allowed disabled:opacity-50"
      >
        A−
      </button>
      <button
        type="button"
        onClick={resetScale}
        aria-label={t("assessment.zoom.resetInterfaceSize")}
        className="flex h-[24px] min-w-[24px] sm:min-w-[24px] items-center justify-center rounded-md border border-secondary/20 px-2 sm:px-2.5 text-xs sm:text-sm font-semibold text-secondary transition-colors hover:bg-secondary/10"
      >
        A
      </button>
      <button
        type="button"
        onClick={increaseScale}
        disabled={scale >= MAX_SCALE}
        aria-label={t("assessment.zoom.increaseInterfaceSize")}
        className="flex h-[24px] min-w-[24px] sm:min-w-[24px] items-center justify-center rounded-md border border-secondary/20 px-2 sm:px-2.5 text-xs sm:text-sm font-semibold text-secondary transition-colors hover:bg-secondary/10 disabled:cursor-not-allowed disabled:opacity-50"
      >
        A+
      </button>
      <div className="hidden sm:block min-w-10 text-center text-sm font-medium text-secondary/70">
        {Math.round(scale * 100)}%
      </div>
    </div>
  );
};

export default ZoomControls;
